import { useRef, useState } from 'react'
import { Upload, Loader2, Trash2, Link2, AlertCircle } from 'lucide-react'
import { uploadImage } from '@/features/admin/content/landingContentApi'

/**
 * Campo de imagen reutilizable (CMS del landing). La imagen se **sube al backend**
 * (almacenamiento real) y se guarda su URL; si el backend no está disponible, cae
 * a un respaldo local para no dejar el botón muerto. También se puede pegar una URL.
 *
 * UX: cuando no hay imagen, el recuadro funciona como zona para subir. Cuando ya
 * hay una, se muestra la miniatura y las acciones cambian a "Cambiar" y "Quitar"
 * (el botón "Subir imagen" deja de tener sentido y no se repite).
 */
export function ImageField({
  label,
  value,
  onChange,
  aspect = 'aspect-video',
  accept = 'image/*',
}: {
  label: string
  value: string
  onChange: (v: string) => void
  /** Compat: ya no se redimensiona en cliente; se conserva por firma. */
  maxDim?: number
  aspect?: string
  /** Tipos aceptados por el input (ej. ".svg,image/svg+xml"). */
  accept?: string
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [showUrl, setShowUrl] = useState(false)

  const pick = () => {
    if (busy) return
    fileRef.current?.click()
  }

  const handleFile = async (file?: File) => {
    if (!file) return
    setBusy(true)
    setNote(null)
    try {
      onChange(await uploadImage(file))
    } catch (err) {
      // Antes se caía a un respaldo base64 local que enmascaraba el fallo y, para
      // imágenes grandes, incrustaba >1 MB en el contenido (rompiendo luego el
      // guardado). Ahora se muestra el motivo real de la subida fallida.
      setNote(err instanceof Error ? err.message : 'No se pudo subir la imagen.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <div>
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      <div className="mt-1.5 flex items-start gap-3">
        {/* Recuadro/miniatura — clic = subir/cambiar */}
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          aria-label={value ? 'Cambiar imagen' : 'Subir imagen'}
          className={`relative grid w-28 shrink-0 place-items-center overflow-hidden rounded-lg border-2 border-dashed bg-surface transition ${aspect} ${
            value ? 'border-line' : 'border-line text-ink-subtle hover:border-brand hover:text-brand'
          } ${busy ? 'cursor-wait' : ''}`}
        >
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex flex-col items-center gap-1 px-2 text-center">
              <Upload className="size-5" />
              <span className="text-[10px] font-medium">Subir imagen</span>
            </span>
          )}
          {busy && (
            <span className="absolute inset-0 grid place-items-center bg-black/50">
              <Loader2 className="size-5 animate-spin text-white" />
            </span>
          )}
        </button>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              className="text-xs font-medium text-brand hover:text-brand-dark disabled:opacity-50"
            >
              {busy ? 'Subiendo…' : value ? 'Cambiar' : 'Subir imagen'}
            </button>
            {value && !busy && (
              <button
                type="button"
                onClick={() => onChange('')}
                className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted hover:text-danger"
              >
                <Trash2 className="size-3.5" /> Quitar
              </button>
            )}
          </div>

          {/* URL como opción secundaria */}
          <button
            type="button"
            onClick={() => setShowUrl((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-subtle hover:text-ink"
          >
            <Link2 className="size-3" /> {showUrl ? 'Ocultar URL' : 'o pegar una URL'}
          </button>
          {showUrl && (
            <input
              value={value}
              placeholder="https://…"
              onChange={(e) => onChange(e.target.value)}
              className="w-full rounded-lg border border-line bg-white px-2.5 py-1.5 text-xs text-ink outline-none focus:border-brand"
            />
          )}

          {note && (
            <p className="inline-flex items-center gap-1 text-[11px] font-medium text-ink-muted">
              <AlertCircle className="size-3" /> {note}
            </p>
          )}
        </div>
      </div>

      <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
    </div>
  )
}
